import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { GroupType, WorkOutcomeFragment } from "@/generated/graphql";

import {
  initEnrollmentIdsByUsers,
  initMarkingGroups,
} from "./init-marking-groups";
import {
  LoadGroupsPayload,
  MarkingGroup,
  MarkingViewState,
  MarkingWorkOutcome,
} from "./types";

/** `mark` slice state */
export interface MarkSliceState {
  /** Current Marker's user ID. */
  currentMarkerId: string | null;
  /** Selected marking group ID */
  selectedMarkingGroupId: string | null;
  /** All Students and Groups and their Work Outcomes. */
  workOutcomes: MarkingWorkOutcome[];
  /**
   * Filtered QuestionBlock NODES displayed for marking. Empty array will show
   * no questions.
   */
  displayedNodeIds: string[];
  /** All Marking Groups */
  groups: MarkingGroup[];
  /** Group Setup type. This setting is only applicable in MFA Marking context. */
  groupType: GroupType | null;
  /** Switch state between personal and universal views. */
  viewState: MarkingViewState;
  /** Mapping of user ID to enrollment ID for quick lookup. */
  enrollmentIdsByUserId: Record<string, string>;
  /** Whether double-blind marking is enabled.  */
  isDoubleBlindMarking: boolean;
  /** Work Outcomes that have been double marked. */
  doubleMarkedWorkOutcomes?: MarkingWorkOutcome[];
}

export const initialState: MarkSliceState = {
  currentMarkerId: null,
  displayedNodeIds: [],
  workOutcomes: [],
  groups: [],
  groupType: null,
  viewState: "Universal", // default to universal
  isDoubleBlindMarking: false,
  selectedMarkingGroupId: null,
  enrollmentIdsByUserId: {},
};

export const markSlice = createSlice({
  name: "mark",
  initialState,
  reducers: {
    /** Initialise marking filters and groups. */
    loadMarkingGroups(state, action: PayloadAction<LoadGroupsPayload>) {
      state.groups = initMarkingGroups(action.payload);
      state.enrollmentIdsByUserId = initEnrollmentIdsByUsers(
        action.payload.enrollments
      );
      state.workOutcomes = action.payload.workOutcomes;
      state.groupType = action.payload.groupType;
      state.currentMarkerId = action.payload.markerId;
      state.isDoubleBlindMarking = action.payload.isDoubleBlindMarking ?? false;
      state.doubleMarkedWorkOutcomes = undefined;
    },
    /** Update and sync view state when the teacher navigates to mark a different student. */
    syncViewStateForSelectedStudent(
      state,
      action: PayloadAction<string | null>
    ) {
      const studentId = action.payload;
      const currentMarkerId = state.currentMarkerId;
      const groupType = state.groupType;
      // If double blind marking, always show universal view
      if (state.isDoubleBlindMarking) {
        state.viewState = "Universal";
        return;
      }

      // Handle null cases - should not happen normally
      if (studentId === null || groupType === null) {
        state.selectedMarkingGroupId = null;
        state.viewState = "Universal";
        return;
      }

      // Find the relevant marking group based on group type
      let studentGroup: MarkingGroup | undefined;

      if (groupType === GroupType.Student) {
        // Student-based groups: student belongs to exactly one group
        // We select this group regardless of who the marker is
        studentGroup = state.groups.find((group) =>
          group.members.some((member) => member.id === studentId)
        );
      } else if (groupType === GroupType.Question) {
        // Question-based groups: student can be in multiple groups
        // We only select the group if the current user is the marker
        studentGroup = state.groups.find(
          (group) =>
            group.markerId === currentMarkerId &&
            group.members.some((member) => member.id === studentId)
        );
      }

      state.selectedMarkingGroupId = studentGroup?.id ?? null;

      // Update view state based on whether we found a matching group
      if (!studentGroup) {
        // No matching group found - switch to universal view
        state.viewState = "Universal";
      } else if (
        studentGroup.markerId === currentMarkerId ||
        studentGroup.groupMembers?.some((member) =>
          member.groupMarkers?.some((gm) => gm.marker?.id === currentMarkerId)
        )
      ) {
        // Current user is the marker for this group - switch to personal view
        state.viewState = "Personal";
      } else {
        // Group found but current user is not the marker - stay in universal view
        state.viewState = "Universal";
      }
    },
    setDisplayedNodeIds(state, action: PayloadAction<string[]>) {
      state.displayedNodeIds = action.payload;
    },
    /** Set marker view level. */
    setViewState(state, action: PayloadAction<"Personal" | "Universal">) {
      state.viewState = action.payload;
    },
    setSelectedMarkingGroupId(state, action: PayloadAction<string>) {
      state.selectedMarkingGroupId = action.payload;
    },
    setDoubleMarkedWorkOutcomes(
      state,
      action: PayloadAction<MarkingWorkOutcome[]>
    ) {
      state.doubleMarkedWorkOutcomes = action.payload;
    },
    updateWorkOutcomeScoreBreakdown(
      state,
      action: PayloadAction<{
        workOutcomeId: string;
        scoreBreakdown: WorkOutcomeFragment["scoreBreakdown"];
      }>
    ) {
      const { workOutcomeId, scoreBreakdown } = action.payload;
      state.workOutcomes = state.workOutcomes.map((work) =>
        work.id === workOutcomeId ? { ...work, scoreBreakdown } : work
      );
    },
    updateWorkOutcomeMarkingSubmissionOutcome(
      state,
      action: PayloadAction<{
        workOutcomeId: string;
        markingSubmissionOutcome: { id: string } | null;
      }>
    ) {
      const { workOutcomeId, markingSubmissionOutcome } = action.payload;
      state.workOutcomes = state.workOutcomes.map((wo) =>
        wo.id === workOutcomeId ? { ...wo, markingSubmissionOutcome } : wo
      );
    },
  },
});

export const {
  loadMarkingGroups,
  syncViewStateForSelectedStudent,
  setDisplayedNodeIds,
  setViewState,
  setSelectedMarkingGroupId,
  setDoubleMarkedWorkOutcomes,
  updateWorkOutcomeScoreBreakdown,
  updateWorkOutcomeMarkingSubmissionOutcome,
} = markSlice.actions;

export const markReducer = markSlice.reducer;
